import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { createSnack } from 'snackbar/snackbarSlice';
import { useGetFromLocationsQuery } from '../../reconsignmentsEndpoints';

// Map UI-friendly inventory status values to API-expected enum values
const INVENTORY_STATUS_MAP = {
  ALL: null,
  Available: 'AVAILABLE',
  'In Transit': 'INTRANSIT',
};

// Map UI-friendly consignment status values to API-expected enum values
const CONSIGNMENT_STATUS_MAP = {
  ALL: 'ALL',
  Active: 'ACTIVE',
  Controlled: 'CONTROLLED',
  Inactive: 'INACTIVE',
  // Add any other consignment status mappings
};

/**
 * Custom hook for fetching and managing locations data
 *
 * @param {Object} reconsignmentState The current reconsignment state
 * @returns {Object} Locations data and loading state
 */
export default function useLocationsData(safeReconsignmentState) {
  const dispatch = useDispatch();
  const [noInventoryForBarrels, setNoInventoryForBarrels] = useState(false);
  const [manualQueryKey, setManualQueryKey] = useState('initial');
  const [manualQueryParams, setManualQueryParams] = useState(null);

  useEffect(() => {
    if (safeReconsignmentState.product?.productId) {
      const newParams = {
        InventoryOwner: {
          CompanyCustomerIdName: safeReconsignmentState.inventoryOwner?.companyCustomerIdName,
          DataCompanyId: safeReconsignmentState.inventoryOwner?.dataCompanyId,
        },
        OriginIds: safeReconsignmentState.origin?.map(o => o.originId) || [],
        ProductId: safeReconsignmentState.product.productId,
        // only include Rvp if not the default "Any"
        ...(safeReconsignmentState.rvp && safeReconsignmentState.rvp !== 'Any'
          ? { Rvp: safeReconsignmentState.rvp }
          : {}),
      };

      // only include ConsignmentStatus if not "ALL"
      if (
        safeReconsignmentState.consignmentStatus &&
        safeReconsignmentState.consignmentStatus !== 'ALL'
      ) {
        const apiConsignmentValue =
          CONSIGNMENT_STATUS_MAP[safeReconsignmentState.consignmentStatus] ||
          safeReconsignmentState.consignmentStatus.replace(/\s+/g, '').toUpperCase();
        newParams.ConsignmentStatus = apiConsignmentValue;
      }

      // only include InventoryStatus if not "ALL"
      if (
        safeReconsignmentState.inventoryStatus &&
        safeReconsignmentState.inventoryStatus !== 'ALL'
      ) {
        const apiInventoryValue =
          INVENTORY_STATUS_MAP[safeReconsignmentState.inventoryStatus] ||
          safeReconsignmentState.inventoryStatus.replace(/\s+/g, '').toUpperCase();
        newParams.inventoryStatus = apiInventoryValue;
      }

      setManualQueryParams(newParams);

      const consignmentKey = newParams.ConsignmentStatus || 'ALL';
      const inventoryKey = newParams.inventoryStatus || 'ALL';
      setManualQueryKey(`${newParams.ProductId}-${consignmentKey}-${inventoryKey}`);
    }
  }, [safeReconsignmentState]);

  // API query
  const {
    data: locationsData = [],
    isFetching: isLoading,
    isError,
    error: locationsError,
  } = useGetFromLocationsQuery(
    {
      manualQueryKey,
      params: manualQueryParams,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !manualQueryParams,
    }
  );

  // Handle API errors
  useEffect(() => {
    if (isError && locationsError) {
      let errorMessage = 'Error loading source locations. Please try again or contact support.';

      if (locationsError.data) {
        if (
          locationsError.data.title &&
          locationsError.data.title.includes('do not have inventory for your selected barrels')
        ) {
          setNoInventoryForBarrels(true);
          return;
        }

        if (locationsError.data.title) {
          errorMessage = locationsError.data.title;
        } else if (typeof locationsError.data === 'string') {
          errorMessage = locationsError.data;
        }
      } else if (locationsError.error) {
        errorMessage = locationsError.error;
      }

      dispatch(
        createSnack({
          message: errorMessage,
          options: {
            autoHideDuration: 10000,
            variant: 'error',
          },
        })
      );
    } else {
      setNoInventoryForBarrels(false);
    }
  }, [isError, dispatch, locationsError]);

  const hasLocations = locationsData && locationsData.length > 0;

  return {
    hasLocations,
    isLoading,
    locationsData,
    noInventoryForBarrels,
  };
}
